import type { LessonAiProgressUpdate } from "./lesson-ai-progress";

export type AiProgressMode = "generate" | "refine" | "template-generate" | "template-refine" | "quiz-generate";

type Props = {
  mode: AiProgressMode;
  progress: LessonAiProgressUpdate;
};

function phaseStatus(index: number, activeIndex: number): "done" | "active" | "pending" {
  if (index < activeIndex) return "done";
  if (index === activeIndex) return "active";
  return "pending";
}

function progressCopy(mode: AiProgressMode): {
  eyebrow: string;
  headline: string;
  lede: string;
  foot: string;
} {
  if (mode === "quiz-generate") {
    return {
      eyebrow: "AI quiz studio",
      headline: "Creating your quiz",
      lede: "Hang tight. We're drafting questions from the lessons before this spot. This usually takes about 20–40 seconds.",
      foot: "Working. We'll open the quiz editor when the draft is ready.",
    };
  }
  if (mode === "template-generate") {
    return {
      eyebrow: "AI course studio",
      headline: "Assembling your course path",
      lede: "Hang tight. We're picking and ordering lessons from your library. This usually takes about 10–25 seconds.",
      foot: "Working. We'll fill in the draft when the sequence is ready.",
    };
  }
  if (mode === "template-refine") {
    return {
      eyebrow: "AI course studio",
      headline: "Refining your course path",
      lede: "Updating the lesson sequence based on your follow-up.",
      foot: "Working. We'll update the draft when ready.",
    };
  }
  if (mode === "refine") {
    return {
      eyebrow: "AI lesson studio",
      headline: "Evolving your lesson",
      lede: "Hang tight. We're drafting your lesson. This usually takes about 20–45 seconds.",
      foot: "Working for this long. You can keep this tab open; we'll update the preview when the draft is ready.",
    };
  }
  return {
    eyebrow: "AI lesson studio",
    headline: "Creating your lesson",
    lede: "Hang tight. We're drafting your lesson. This usually takes about 20–45 seconds.",
    foot: "Working for this long. You can keep this tab open; we'll update the preview when the draft is ready.",
  };
}

export default function LessonAiProgressPanel({ mode, progress }: Props) {
  const { phases, phaseIndex, phase, elapsedMs } = progress;
  const progressPct = Math.min(100, Math.round(((phaseIndex + 1) / phases.length) * 100));
  const copy = progressCopy(mode);
  const elapsedSec = Math.max(1, Math.round(elapsedMs / 1000));

  return (
    <section className="teacher-ai-progress" role="status" aria-live="polite" aria-busy="true">
      <div className="teacher-ai-progress-header">
        <div>
          <p className="teacher-ai-progress-eyebrow">{copy.eyebrow}</p>
          <h2 className="teacher-ai-progress-title">{copy.headline}</h2>
          <p className="teacher-ai-progress-lede muted">{copy.lede}</p>
        </div>
        <div className="teacher-ai-progress-spinner" aria-hidden="true" />
      </div>

      <div className="teacher-ai-progress-bar" aria-hidden="true">
        <div className="teacher-ai-progress-bar-fill" style={{ width: `${progressPct}%` }} />
      </div>

      <p className="teacher-ai-progress-active">
        <span className="teacher-ai-progress-active-label">{phase.label}</span>
        <span className="teacher-ai-progress-active-detail muted">{phase.detail}</span>
      </p>

      <ol className="teacher-ai-progress-steps">
        {phases.map((step, index) => {
          const status = phaseStatus(index, phaseIndex);
          return (
            <li
              key={step.id}
              className={`teacher-ai-progress-step teacher-ai-progress-step--${status}`}
            >
              <span className="teacher-ai-progress-step-marker" aria-hidden="true">
                {status === "done" ? "✓" : status === "active" ? "…" : ""}
              </span>
              <span className="teacher-ai-progress-step-text">{step.label}</span>
            </li>
          );
        })}
      </ol>

      <p className="teacher-ai-progress-foot muted">
        Working for {elapsedSec}s.{" "}
        {mode === "quiz-generate"
          ? "We'll open the quiz editor when the draft is ready."
          : mode.startsWith("template")
            ? "We'll update the draft when ready."
            : "You can keep this tab open; we'll update the preview when the draft is ready."}
      </p>
    </section>
  );
}
